import { Box, IconDelete, Text, useDialogController, useToast } from '@machobear/design-system'
import { useDispatch } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import { show as showVideoLightBox } from '@/components/video-light-box'
import { colors } from '@/styles'
import embeddedLinkGenerators from '@/utils/video-embedded-links'
import { VideoLinkPlayer } from '@/components/video-player'
import { VideoLink } from '../../types'
import ExternalLinkModal from '../components/external-link-modal'
import VideoThumbnail from '../components/video-thumbnail'
import {
  getMatterportThumbnail,
  getMetarealThumbnail,
  getVimeoThumbnail,
  getYoutubeThumbnail
} from '../util'

interface Props {
  label?: string
  listingId: string
  videoData: VideoLink
  videoType: '2d' | '3d'
  externalLink?: boolean
  [key: string]: any
}

const LinkedVideoPreview = ({
  label,
  listingId,
  videoData,
  videoType,
  externalLink,
  ...rest
}: Props) => {
  const toast = useToast()
  const dispatch = useDispatch()
  const { visible, showModal: showExternalLinkModal, closeModal } = useDialogController()

  const [thumbnail, setThumbnail] = useState<string | null>(null)

  useEffect(() => {
    if (!videoData) {
      return
    }

    const fetchThumbnail = async () => {
      let thumbnailUrl = null

      if (videoData.link_service === 'youtube') {
        thumbnailUrl = getYoutubeThumbnail(videoData.link_url)
      } else if (videoData.link_service === 'vimeo') {
        thumbnailUrl = await getVimeoThumbnail(videoData.link_url)
      } else if (videoData.link_service === 'matterport') {
        thumbnailUrl = getMatterportThumbnail(videoData.link_url)
      } else if (videoData.link_service === 'metareal') {
        thumbnailUrl = getMetarealThumbnail(videoData.link_url)
      }
      // Note: Unable to find any public APIs for fetching
      // thumbnails for eyexpo and iguide 3d tours

      setThumbnail(thumbnailUrl)
    }

    fetchThumbnail()
  }, [videoData])

  const onDelete = async () => {
    try {
      await dispatch.listing.removeVideoLink({
        listingId,
        videoType
      })
    } catch (error) {
      toast({ message: 'Unable to delete video link', tone: 'critical' })
    }
  }

  const playbackUrl = useMemo(() => {
    if (videoData) {
      const { link_url, link_service } = videoData
      return embeddedLinkGenerators[link_service as 'youtube' | 'vimeo']?.(link_url) ?? link_url
    }
  }, [videoData])

  const handleClickThumbnail = () => {
    if (externalLink) {
      showExternalLinkModal()
    } else {
      showVideoLightBox({
        videoLocation: playbackUrl,
        controls: true,
        autoPlay: true,
        PlayerComponent: VideoLinkPlayer
      })
    }
  }

  return (
    <>
      <ExternalLinkModal
        visible={visible}
        closeModal={closeModal}
        externalUrl={videoData.link_url}
      />

      <Box
        position="relative"
        display="flex"
        justifyContent="spaceBetween"
        border="borderNeutralLight"
        borderRadius="small"
        width="full"
        paddingY="xxsmall"
        paddingX="small"
        {...rest}
      >
        <Box width="full" display="flex" alignItems="center" gap="small">
          <VideoThumbnail
            videoType={videoType}
            thumbnail={thumbnail}
            handleClickThumbnail={handleClickThumbnail}
          />
          <Box
            width="full"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            gap="small"
            style={{ minWidth: '0' }}
          >
            {label && (
              <Box width="full" style={{ minWidth: '0' }}>
                <Text size="small" weight="medium" maxLines={1}>
                  {label}
                </Text>
              </Box>
            )}
          </Box>
          <Box display="flex" justifyContent="center" cursor="pointer" alignItems="center">
            <Box
              padding="xxxsmall"
              style={{
                backgroundColor: colors.backgroundCriticalSoft,
                borderRadius: '50%'
              }}
              onClick={e => {
                onDelete()
                e.stopPropagation()
              }}
              role="button"
            >
              <IconDelete size="xsmall" tone="brandAccent" />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default LinkedVideoPreview
